import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import VisitService from "../../services/VisitService";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

function AuriculotherapyVisits() {
  const { patientID } = useParams();
  const navigate = useNavigate();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCharts, setShowCharts] = useState(false);

  useEffect(() => {
    const loadVisits = async () => {
      try {
        const data = await VisitService.getVisitsByPatientAndTreatmentType(
          patientID,
          "auriculotherapy"
        );

        if (!Array.isArray(data)) {
          setVisits([]);
          return;
        }

        const filtered = data
          .map((visit) => {
            const items = (visit.visitItemDTOS || [])
              .filter((item) => {
                const dto = item.treatmentTypeDTO;
                // Filtriramo stavke koje imaju bar neki od ključnih parametara
                return dto?.weight || dto?.bmi || dto?.waist || dto?.hipCircumference;
              })
              .map((item) => {
                const dto = item.treatmentTypeDTO;
                return {
                  ...item,
                  weight: dto?.weight ?? "-",
                  bmi: dto?.bmi ?? "-",
                  waist: dto?.waist ?? "-",
                  bodyFat: dto?.bodyFat ?? "-",
                  visceralFat: dto?.visceralFat ?? "-",
                  protein: dto?.protein ?? "-",
                  // NOVI PARAMETRI
                  hipCircumference: dto?.hipCircumference ?? "-",
                  abdomenCircumference: dto?.abdomenCircumference ?? "-",
                  chestCircumference: dto?.chestCircumference ?? "-",
                  dietPlan: dto?.dietPlan ?? "Nema unetog plana",
                };
              });
            return items.length > 0 ? { ...visit, visitItemDTOS: items } : null;
          })
          .filter(Boolean)
          .sort((a, b) => new Date(a.visitDate) - new Date(b.visitDate));

        setVisits(filtered);
      } catch (error) {
        console.error("Greška:", error);
        setVisits([]);
      } finally {
        setLoading(false);
      }
    };

    loadVisits();
  }, [patientID]);

  if (loading) return <p>Učitavanje podataka...</p>;

  const patientName =
    visits.length > 0
      ? `${visits[0].patientDTO?.name || ""} ${visits[0].patientDTO?.surname || ""}`.trim()
      : "Pacijent";

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("sr-RS");

  // 📊 Priprema podataka za grafikone (uključeni i novi obimi)
  const chartData = visits.map((visit) => {
    const item = visit.visitItemDTOS[0];
    const parseVal = (val) => (val === "-" ? null : parseFloat(val));
    return {
      date: formatDate(visit.visitDate),
      weight: parseVal(item.weight),
      bmi: parseVal(item.bmi),
      waist: parseVal(item.waist),
      bodyFat: parseVal(item.bodyFat),
      visceralFat: parseVal(item.visceralFat),
      protein: parseVal(item.protein),
      hip: parseVal(item.hipCircumference),
      abdomen: parseVal(item.abdomenCircumference),
      chest: parseVal(item.chestCircumference),
    };
  });

  // --- EXPORT CSV (Ažuriran sa novim kolonama) ---
  const exportToCSV = () => {
    if (visits.length === 0) {
      alert("Nema merenja za eksport.");
      return;
    }

    const headers = [
      "Datum", "Težina (kg)", "BMI", "Struk (cm)", "Kukovi (cm)", 
      "Stomak (cm)", "Grudi (cm)", "Body Fat (%)", "Visceral Fat", "Protein (%)", "Jelovnik"
    ];

    const rows = visits.flatMap((visit) =>
      visit.visitItemDTOS.map((item) => [
        `"${formatDate(visit.visitDate)}"`,
        `"${item.weight}"`,
        `"${item.bmi}"`,
        `"${item.waist}"`,
        `"${item.hipCircumference}"`,
        `"${item.abdomenCircumference}"`,
        `"${item.chestCircumference}"`,
        `"${item.bodyFat}"`,
        `"${item.visceralFat}"`,
        `"${item.protein}"`,
        `"${item.dietPlan.replace(/"/g, '""')}"`, // Escape navodnika u tekstu
      ])
    );

    const csvContent = "\uFEFF" + [headers, ...rows].map((e) => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `aurikuloterapija_${patientName.replace(/\s+/g, '_')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{ padding: "30px", textAlign: "center", fontFamily: "Arial, sans-serif" }}>
      <h2 style={{ marginBottom: "25px", color: "#333" }}>
        Pratite napredak: {patientName}
      </h2>

      {visits.length > 0 && (
        <div style={{ marginBottom: "20px" }}>
          <button onClick={exportToCSV} style={buttonStyle("#007bff")}>📥 Export CSV</button>
          <button onClick={() => setShowCharts(!showCharts)} style={buttonStyle("#28a745")}>
            {showCharts ? "❌ Sakrij grafikone" : "📊 Prikaži grafikone"}
          </button>
        </div>
      )}

      {visits.length === 0 ? (
        <p style={{ fontStyle: "italic", color: "#666" }}>Nema evidentiranih merenja.</p>
      ) : (
        <div style={{ overflowX: "auto", margin: "0 auto", maxWidth: "1100px" }}>
          <table style={tableStyle}>
            <thead style={{ backgroundColor: "#007bff", color: "white" }}>
              <tr>
                <th style={thStyle}>Datum</th>
                <th style={thStyle}>kg</th>
                <th style={thStyle}>BMI</th>
                <th style={thStyle}>Struk</th>
                <th style={thStyle}>Kukovi</th>
                <th style={thStyle}>Stomak</th>
                <th style={thStyle}>Grudi</th>
                <th style={thStyle}>Fat %</th>
                <th style={thStyle}>Jelovnik / Plan</th>
              </tr>
            </thead>
            <tbody>
              {visits.map((visit) =>
                visit.visitItemDTOS.map((item, i) => (
                  <tr key={`${visit.visitID}-${i}`} style={rowStyle}>
                    <td style={tdStyle}><b>{formatDate(visit.visitDate)}</b></td>
                    <td style={tdStyle}>{item.weight}</td>
                    <td style={tdStyle}>{item.bmi}</td>
                    <td style={tdStyle}>{item.waist}</td>
                    <td style={tdStyle}>{item.hipCircumference}</td>
                    <td style={tdStyle}>{item.abdomenCircumference}</td>
                    <td style={tdStyle}>{item.chestCircumference}</td>
                    <td style={tdStyle}>{item.bodyFat}</td>
                    <td style={{ ...tdStyle, textAlign: "left", fontSize: "0.85em", maxWidth: "250px" }}>
                      {item.dietPlan}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showCharts && (
        <div style={{ marginTop: "40px", maxWidth: "900px", marginInline: "auto" }}>
          <h3>Vizuelni prikaz trendova</h3>
          {renderChart(chartData, "weight", "Težina (kg)", "#007bff")}
          {renderChart(chartData, "waist", "Obim struka (cm)", "#ff9800")}
          {renderChart(chartData, "hip", "Obim kukova (cm)", "#e91e63")}
          {renderChart(chartData, "bodyFat", "Procenat masti (%)", "#dc3545")}
        </div>
      )}

      <button onClick={() => navigate(-1)} style={buttonStyle("#dc3545", { marginTop: "30px" })}>
        Nazad
      </button>
    </div>
  );
}

// 🔧 Stilovi i Helperi
const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  borderRadius: "10px",
  overflow: "hidden",
  boxShadow: "0 0 15px rgba(0,0,0,0.1)",
  backgroundColor: "#fff",
};

const thStyle = { padding: "14px 10px", fontWeight: "600", fontSize: "0.9em" };
const tdStyle = { padding: "12px 10px", textAlign: "center", fontSize: "0.9em", borderBottom: "1px solid #eee" };
const rowStyle = { backgroundColor: "#f8f9fa" };

const buttonStyle = (color, extra = {}) => ({
  margin: "5px",
  padding: "10px 20px",
  borderRadius: "8px",
  border: "none",
  backgroundColor: color,
  color: "white",
  fontWeight: "bold",
  cursor: "pointer",
  ...extra,
});

function renderChart(data, key, label, color) {
  const filtered = data.filter((d) => d[key] !== null);
  if (filtered.length === 0) return null;

  return (
    <div style={{ marginBottom: "40px", backgroundColor: "#fff", padding: "15px", borderRadius: "10px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
      <h4 style={{ marginBottom: "15px" }}>{label}</h4>
      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={filtered}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="date" tick={{fontSize: 12}} />
          <YAxis domain={['auto', 'auto']} tick={{fontSize: 12}} />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey={key} name={label} stroke={color} strokeWidth={3} dot={{ r: 6 }} activeDot={{ r: 8 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default AuriculotherapyVisits;